/**
 * TELV1-529 — "Copy integration brief": one clipboard write that turns the
 * embed tab's live state into a handoff a survey programmer can act on
 * without dashboard access. Markdown-formatted so it pastes cleanly into
 * email, Slack and tickets.
 *
 * Pure module on purpose (mirrors embedSnippets.ts): no imports from the
 * component tree, every input passed explicitly — the brief can only ever
 * reflect the same live state the tab renders, and it stays unit-testable.
 */

export interface BriefPlaceholderRow {
  name: string;
  /** The researcher-entered pipe value, '' when still unmapped. */
  value: string;
  /** Platform-native example syntax for this variable, '' when none. */
  example: string;
}

export interface IntegrationBriefInput {
  projectTitle: string;
  projectId: string;
  /** Dashboard deep link to the embed tab (org/ws scoped), '' to omit. */
  dashboardUrl: string;
  platformLabel: string;
  tierLabel: string;
  tierDescription: string;
  pasteHint: string;
  published: boolean;
  /** The main snippet exactly as the tab renders it. */
  primarySnippet: string;
  /** Qualtrics part 2 (question JS), '' when not applicable. */
  secondarySnippet: string;
  /** The once-per-page host script, '' when the platform embeds it already. */
  hostScript: string;
  respondentIdValue: string;
  placeholders: BriefPlaceholderRow[];
  testUrl: string;
  liveUrl: string;
  docsUrl: string;
  language: string;
  /** Wave label tagged onto conversations (?wave=), '' when unset. */
  waveLabel?: string;
  consentShown: boolean;
  inputMethods: string[];
  width: string;
  height: string;
}

/** Escape a value for a markdown table cell (pipes + newlines). */
const cell = (value: string): string => (value || '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');

const codeBlock = (language: string, code: string): string =>
  `\`\`\`${language}\n${code.trim()}\n\`\`\``;

export function buildIntegrationBrief(input: IntegrationBriefInput): string {
  const lines: string[] = [];

  lines.push(`# Tellet embed — integration brief`);
  lines.push('');
  lines.push(`**Project:** ${input.projectTitle} (\`${input.projectId}\`)`);
  if (input.dashboardUrl) lines.push(`**Dashboard:** ${input.dashboardUrl}`);
  lines.push(
    `**Status:** ${
      input.published
        ? 'Published — the snippet is live'
        : 'NOT PUBLISHED — the snippet will not load until the project is published'
    }`,
  );
  // TIER_COPY descriptions are self-contained ("Verified — tested end-to-end
  // …"), so the label is only prepended when the description doesn't already
  // lead with it.
  const tier = input.tierDescription.startsWith(input.tierLabel)
    ? input.tierDescription
    : `${input.tierLabel} — ${input.tierDescription}`;
  lines.push(`**Platform:** ${input.platformLabel} · ${tier}`);
  lines.push('');

  lines.push(`## Where to paste`);
  lines.push('');
  lines.push(input.pasteHint);
  lines.push('');

  lines.push(`## Snippet`);
  lines.push('');
  lines.push(codeBlock('html', input.primarySnippet));
  if (input.secondarySnippet) {
    lines.push('');
    lines.push(`**Part 2 — add to the question's JavaScript (onReady):**`);
    lines.push('');
    lines.push(codeBlock('javascript', input.secondarySnippet));
  }
  if (input.hostScript) {
    lines.push('');
    lines.push(`**Once per page (communication script):**`);
    lines.push('');
    lines.push(codeBlock('html', input.hostScript));
  }
  lines.push('');

  lines.push(`## Respondent ID`);
  lines.push('');
  lines.push(
    `The snippet passes \`respondent_id=${input.respondentIdValue}\`. It must resolve to YOUR respondent identifier — it is how conversations are matched back to survey rows.`,
  );
  lines.push('');

  if (input.placeholders.length > 0) {
    lines.push(`## Question variables to map`);
    lines.push('');
    lines.push(
      `The interview questions reference these variables; each must be piped from the survey (platform syntax shown):`,
    );
    lines.push('');
    lines.push(`| Variable | Current value | ${input.platformLabel} syntax |`);
    lines.push(`| --- | --- | --- |`);
    input.placeholders.forEach((row) => {
      lines.push(
        `| \`%%${cell(row.name)}%%\` | ${
          row.value ? `\`${cell(row.value)}\`` : '**UNMAPPED — action needed**'
        } | ${row.example ? `\`${cell(row.example)}\`` : '—'} |`,
      );
    });
    lines.push('');
  }

  lines.push(`## Settings in the snippet`);
  lines.push('');
  lines.push(`- Language: ${input.language}`);
  if (input.waveLabel) lines.push(`- Wave label: ${input.waveLabel}`);
  lines.push(
    `- Consent screen: ${input.consentShown ? 'shown to respondents' : 'skipped (survey already collected consent)'}`,
  );
  lines.push(`- Response types: ${input.inputMethods.join(', ') || 'default'}`);
  lines.push(`- Size: ${input.width} × ${input.height}`);
  lines.push('');

  lines.push(`## Test before fielding`);
  lines.push('');
  lines.push(`- Safe test link (sandbox — never counts toward results): ${input.testUrl}`);
  lines.push(`- Live link: ${input.liveUrl}`);
  lines.push('');

  lines.push(`## Events & data capture`);
  lines.push('');
  // TELV1-533 C3 — the cheat-sheet lives IN the brief: this document's
  // whole point is a handoff the programmer can act on without dashboard
  // access, and the full docs link below requires a Tellet login.
  lines.push(
    'The embed posts events to the host page via `window.postMessage`. The ones most integrations use:',
  );
  lines.push('');
  lines.push('| Event | When it fires | What to do |');
  lines.push('| --- | --- | --- |');
  lines.push(
    '| `tellet:processing` | The respondent finishes (sandbox tests included) | Release your Next control — analysis continues in the background |',
  );
  lines.push(
    '| `CONVERSATION_COMPLETE` | Right before END; carries the full transcript, a `results_token`, and `stimulus_q<N>` (which creative this respondent saw) | Store the payload (or just the token) in your survey row |',
  );
  lines.push('| `END` | After CONVERSATION_COMPLETE | Legacy continuation trigger |');
  lines.push(
    '| `tellet:ready` | Analysis (and category coding, when configured) is done | Only needed when a later survey page branches on results |',
  );
  lines.push(
    '| `CODING_READY` | Per interview segment; `categories` is cumulative, the final message has `complete: true` | Write codes into your survey data |',
  );
  lines.push(
    '| `CHECKPOINT_REACHED` | A segment finished and the interview paused | Advance your survey, then resume: keep the iframe mounted and post `CHECKPOINT_COMPLETE` to it, or reload the SAME iframe element |',
  );
  lines.push(
    '| `STATUS_CHANGE` / `INPUT` / `OUTPUT` | Progress + session-state bridge | Handled by the standard communication script — pass all keys through unchanged |',
  );
  lines.push('');
  lines.push(
    'Two rules that prevent the common failures: keep the SAME iframe element alive for the whole flow (a rebuilt element starts a new conversation), and store the `results_token` from the completion payload if you want to fetch the summary + codes server-side afterwards (valid 2 hours: `GET /embed/results?token=…`).',
  );
  lines.push('');
  lines.push(
    `Full reference incl. payload shapes and capture strategies (requires a Tellet login): ${input.docsUrl}`,
  );
  lines.push('');

  return lines.join('\n');
}
